import { useQuery, useQueryClient } from '@tanstack/react-query';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { api, setToken } from './lib/api';
import { useSession } from './lib/useSession';
import { credits } from './lib/format';
import { Admin } from './pages/Admin';
import { Dashboard } from './pages/Dashboard';
import { Leaderboard } from './pages/Leaderboard';
import { Login, SignIn } from './pages/Login';
import { MarketPage } from './pages/Market';
import { Markets } from './pages/Markets';
import { Settings } from './pages/Settings';
import { Signup } from './pages/Signup';

export function App() {
  const qc = useQueryClient();
  const token = useSession();
  const me = useQuery({ queryKey: ['me'], queryFn: api.me, enabled: token !== null, retry: false });
  const signedIn = token !== null && !me.isError;

  return (
    <div className="shell">
      <header className="topbar">
        <NavLink to="/" className="brand">
          Exchange<span>play money</span>
        </NavLink>
        <nav>
          <NavLink to="/">Markets</NavLink>
          <NavLink to="/leaderboard">Leaderboard</NavLink>
          {signedIn && <NavLink to="/dashboard">Dashboard</NavLink>}
          {signedIn && <NavLink to="/settings">Settings</NavLink>}
          {signedIn && me.data?.isAdmin && <NavLink to="/admin">Admin</NavLink>}
          {signedIn && me.data && (
            <span className="balance">
              <small>Balance</small>
              {credits(me.data.cash)}
              {me.data.escrow > 0n && <span className="muted"> · {credits(me.data.escrow)} held</span>}
            </span>
          )}
          {signedIn ? (
            <button
              className="signout"
              onClick={() => {
                // Revoke server-side too, so a stolen token cannot outlive
                // the sign-out. Clearing local state alone is not signing out.
                void api.signOut().catch(() => undefined);
                setToken(null);
                qc.clear();
              }}
            >
              Sign out
            </button>
          ) : (
            <>
              <NavLink to="/login">Sign in</NavLink>
              <NavLink to="/signup" className="cta">Sign up</NavLink>
            </>
          )}
        </nav>
      </header>

      <main>
        <Routes>
          <Route path="/" element={<Markets />} />
          <Route path="/markets/:id" element={<MarketPage signedIn={signedIn} />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/dashboard" element={signedIn ? <Dashboard /> : <Navigate to="/login" replace />} />
          <Route path="/settings" element={signedIn ? <Settings /> : <Navigate to="/login" replace />} />
          {/* The old name, kept so existing links and bookmarks still land. */}
          <Route path="/portfolio" element={<Navigate to="/dashboard" replace />} />
          {/*
            Guarded here for the UI's sake only. Every admin route re-checks the
            session server-side, so hiding the link is convenience, never
            security — a non-admin who types the URL sees an empty screen and
            gets 403 from the API.

            The pending case matters: on a hard refresh of /admin the `me` query
            has not resolved yet, so `isAdmin` is undefined. Treating that as
            "not an admin" redirected the actual admin to the home page every
            time they reloaded the page they were working on.
          */}
          <Route
            path="/admin"
            element={
              !signedIn ? (
                <Navigate to="/login" replace />
              ) : me.isPending ? (
                <div className="empty-state">Loading…</div>
              ) : me.data?.isAdmin ? (
                <Admin />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="*" element={<div className="empty-state">No such page.</div>} />
        </Routes>
      </main>

      {/*
        Required on every page, not a footnote. Section 9 of the architecture
        spec: the play-money framing is what keeps this legal to operate, and
        it must be visible wherever someone can place an order.
      */}
      <div className="playmoney">
        Play money only · Credits have no monetary value and cannot be withdrawn or exchanged · 18+
      </div>
    </div>
  );
}
